/**
 * @typedef {import('../../Auth/AuthService/AuthService').default} AuthService
 * @typedef {import('@mooveguru/js-http-client/GetsHttp').default} HttpClient
 * @typedef {import('shared/MarketplaceService/GetsSuggestedVendorCategories').default} GetsSuggestedVendorCategories
 * @typedef {import('shared/MarketplaceService/GetsVendors').default} GetsVendors
 * @typedef {import('shared/MarketplaceService/Vendor').default} Vendor
 * @typedef {import('shared/MarketplaceService/VendorResponse').default} VendorResponse
 * @typedef {Readonly<{ authService: AuthService, httpClient: HttpClient }>} Dependencies
 */
import { toAbsoluteUrl } from '../../../src/utils/toAbsoluteUrl';
import apiUrls from '../../config/local/api-urls';

const baseUrl = 'https://search.homekeepr.com';

/**
 * @implements {GetsSuggestedVendorCategories}
 * @implements {GetsVendors}
 */
export default class MarketplaceService {
	/** @type {Dependencies['authService']} */
	#authService;
	/** @type {Dependencies['httpClient']} */
	#httpClient;

	/**
	 * @param {Dependencies} dependencies
	 */
	constructor(dependencies) {
		this.#authService = dependencies.authService;
		this.#httpClient = dependencies.httpClient;
	}

	/**
	 * @param {string} query
	 * @returns {Promise<string[]>}
	 */
	async getSuggestedVendorCategories(query) {
		const response = await this.#httpClient.get(
			`${baseUrl}/categories?q=${query}`
		);

		if (!response.isOk) {
			throw new Error(
				'Something went wrong trying to get suggested categories.'
			);
		}

		const records = response.body.results.records;

		return records.map(
			/** @param {{ name: string; }} record */ (record) => record.name
		);
	}

	/**
	 * @param {string | string[]} [query]
	 * @param {string} [postalCode]
	 * @param {number} [page]
	 * @param {string} [slug]
	 * @param {{limit?: number, relatedType?: 'tenant' | 'local'}} [options]
	 * @returns {Promise<Readonly<{ categories: string[], pages: number, restricted: boolean, vendors: Vendor[] }>>}
	 */
	async getVendors(
		query = '',
		postalCode = '',
		page = 1,
		slug = '',
		options = {}
	) {
		const limit = options.limit ?? 6;
		const params = new URLSearchParams();
		params.append('limit', String(limit));
		params.append('page', String(page));

		if (Array.isArray(query) && query.length) {
			params.append('categories', query.join(','));
		} else if (query) {
			params.append('category', query);
		}

		if (postalCode) {
			params.append('postal_code', postalCode);
		}

		if (options.relatedType) {
			params.append('related_type', options.relatedType);
		}

		const endpoint = `${apiUrls.offers.root}/${slug}?${params.toString()}`;

		const headers = /** @type {import('node-fetch').Headers} */ (
			/** @type {unknown} */ (new Headers())
		);
		/*
		 * Public slug marketplace pages should resolve vendors by slug.
		 * Sending an auth token makes the API resolve by authenticated user
		 * first, which can reject with unauthorized for this flow.
		 */
		if (!slug) {
			let token;

			try {
				token = this.#authService.getAccessToken();
			} catch (error) {
				/*
				 * Do nothing, error is that the user is not logged in,
				 * since this route can be public, this can be allowed
				 * to fail.
				 */
			}

			if (token) {
				headers.set('Authorization', `Bearer ${token}`);
			}
		}

		const response = await this.#httpClient.get(endpoint, headers);

		if (!response.isOk) {
			throw new Error('Something went wrong trying to get your vendors.');
		}

		return {
			categories:
				response.body.category_list ?? response.body.categoryList ?? [],
			pages: response.body.pages,
			restricted: response.body.restricted ?? false,
			vendors: response.body.offers.map(
				/** @param {VendorResponse} record */
				(record) => ({
					category: record.category,
					description: record?.description || null,
					headline: record?.title ?? null,
					id: record.id,
					logoUrl: MarketplaceService.#resolveLogoUrl(record.image),
					name: record.vendor.name,
					phoneNumber:
						record.vendor?.phone_number ??
						record.vendor?.phoneNumber ??
						null,
					postalCode: record.postal_code,
					related: record.related ?? null,
					source: record.source,
					websiteUrl: record.website_url ?? record.websiteUrl ?? null,
				})
			),
		};
	}

	/**
	 * @param {{e_tag:string, path:string} | string | null} image
	 * @returns {string | null}
	 */
	static #resolveLogoUrl(image) {
		if (!image) {
			return null;
		}

		if (typeof image === 'string') {
			return image;
		}

		return toAbsoluteUrl(image.path);
	}
}
